import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Loader2, Save, FileText, FileSpreadsheet, FileCode2,
  Download, Pencil, ReceiptText, AlertTriangle, CheckCircle2, Send, Undo2,
} from "lucide-react";

const peppolFromTrn = (trn) => {
  const d = (trn || "").replace(/\D/g, "");
  return d.length >= 10 ? `0235:${d.slice(0, 10)}` : "";
};
const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const VAT_OPTS = [
  { value: "standard", label: "5% standard" },
  { value: "zero", label: "0% zero-rated" },
  { value: "exempt", label: "Exempt" },
];
const EMPTY_LINE = { description: "", qty: 1, unit: "unit", unit_price: 0, vat_rate: "standard" };
const EMPTY = {
  customer: { company_name: "", trn: "", address: "", city: "", peppol_id: "" },
  lines: [{ ...EMPTY_LINE }],
  discount: 0, discount_label: "Discount", issue_date: "", due_date: "", po_reference: "", notes: "",
};

export default function StandardInvoices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list");   // list | edit
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState({});
  const [subBusy, setSubBusy] = useState("");

  const load = useCallback(() => {
    Promise.all([
      api.get("/einvoice/standard-invoices"),
      api.get("/einvoice/submissions"),
    ]).then(([r, s]) => {
      setRows(r.data.invoices); setSub(r.data.subscription);
      const map = {};
      (s.data.submissions || []).forEach((x) => { if (x.doc_kind === "standard") map[x.doc_id] = x; });
      setSubs(map);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fullService = sub?.plan === "full_service";
  const submitJam = async (id) => {
    setSubBusy(id);
    try {
      const r = await api.post(`/einvoice/standard-invoices/${id}/submit-wafeq`);
      setSubs((m) => ({ ...m, [id]: r.data }));
      toast.success("Submitted to the FTA via Wafeq — clearance in progress");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const s = await api.get(`/einvoice/submissions/standard/${id}`);
          if (s.data.submission) {
            setSubs((m) => ({ ...m, [id]: s.data.submission }));
            const st = s.data.submission.status;
            if (["APPROVED", "REPORTED", "DELIVERED", "REJECTED", "INVALID", "NOT_COMPLIANT", "FAILED", "ACKNOWLEDGED", "ERROR"].includes(st)) return;
          }
        } catch { /* ignore */ }
        if (tries < 8) setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit to the FTA");
      try { const s = await api.get(`/einvoice/submissions/standard/${id}`); if (s.data.submission) setSubs((m) => ({ ...m, [id]: s.data.submission })); } catch { /* ignore */ }
    }
    finally { setSubBusy(""); }
  };

  const newInvoice = () => {
    setEditId(null);
    setForm({ ...EMPTY, lines: [{ ...EMPTY_LINE }], issue_date: new Date().toISOString().slice(0, 10) });
    setMode("edit");
  };
  const editInvoice = async (id) => {
    try {
      const { data } = await api.get(`/einvoice/standard-invoices/${id}`);
      setEditId(id);
      setForm({
        customer: data.customer || EMPTY.customer,
        lines: (data.lines || []).map((l) => ({ description: l.description, qty: l.qty, unit: l.unit || "unit", unit_price: l.unit_price, vat_rate: l.vat_rate || "standard" })),
        discount: data.discount || 0, discount_label: data.discount_label || "Discount",
        issue_date: data.issue_date || "", due_date: data.due_date || "",
        po_reference: data.po_reference || "", notes: data.notes || "",
      });
      setMode("edit");
    } catch { toast.error("Could not open invoice"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try { await api.delete(`/einvoice/standard-invoices/${id}`); toast.success("Invoice deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const setCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  // live totals
  let stdNet = 0, zeroNet = 0, exemptNet = 0;
  form.lines.forEach((l) => {
    const net = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0);
    if (l.vat_rate === "zero") zeroNet += net;
    else if (l.vat_rate === "exempt") exemptNet += net;
    else stdNet += net;
  });
  const discount = parseFloat(form.discount) || 0;
  const taxable = Math.max(0, stdNet - discount);
  const vat = taxable * 0.05;
  const nontax = zeroNet + exemptNet;
  const total = taxable + nontax + vat;

  const derivedPeppol = peppolFromTrn(form.customer.trn);
  const trnDigits = (form.customer.trn || "").replace(/\D/g, "");
  const trnWarn = form.customer.trn && trnDigits.length !== 15;
  const peppolWarn = form.customer.peppol_id && derivedPeppol && form.customer.peppol_id !== derivedPeppol;

  const save = async () => {
    const valid = form.lines.filter((l) => (l.description || "").trim() || parseFloat(l.unit_price));
    if (valid.length === 0) { toast.error("Add at least one invoice line"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        discount: parseFloat(form.discount) || 0,
        lines: form.lines.map((l) => ({ ...l, qty: parseFloat(l.qty) || 0, unit_price: parseFloat(l.unit_price) || 0 })),
      };
      if (editId) {
        await api.put(`/einvoice/standard-invoices/${editId}`, payload);
        toast.success("Invoice updated");
      } else {
        const r = await api.post("/einvoice/standard-invoices", payload);
        setSub(r.data.subscription);
        toast.success(`Invoice ${r.data.invoice.invoice_number} created`);
      }
      setMode("list"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save invoice");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  if (mode === "edit") {
    return (
      <div data-testid="standard-invoice-editor" className="max-w-5xl">
        <button onClick={() => setMode("list")} data-testid="std-back-btn" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Back to invoices</button>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">{editId ? "Edit invoice" : "New standard invoice"}</h1>
        <p className="text-sm text-muted-foreground mb-6">A straightforward tax invoice — no contract or BOQ needed. Your company details come from your profile.</p>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Customer */}
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Customer (Bill to)</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label>Company / customer name</Label><Input data-testid="std-cust-name" value={form.customer.company_name} onChange={(e) => setCust("company_name", e.target.value)} placeholder="Acme Trading LLC" /></div>
                <div>
                  <Label>TRN (optional)</Label>
                  <Input data-testid="std-cust-trn" value={form.customer.trn} onChange={(e) => { const trn = e.target.value; const d = peppolFromTrn(trn); setForm((f) => ({ ...f, customer: { ...f.customer, trn, peppol_id: (!f.customer.peppol_id || f.customer.peppol_id === peppolFromTrn(f.customer.trn)) && d ? d : f.customer.peppol_id } })); }} placeholder="15 digits" />
                  {trnWarn && <p data-testid="std-trn-warn" className="text-[11px] text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> TRN should be exactly 15 digits</p>}
                </div>
                <div>
                  <Label>Peppol ID (optional)</Label>
                  <Input data-testid="std-cust-peppol" value={form.customer.peppol_id} onChange={(e) => setCust("peppol_id", e.target.value)} placeholder="auto from TRN" />
                  {peppolWarn && <p data-testid="std-peppol-warn" className="text-[11px] text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Doesn't match TRN ({derivedPeppol})</p>}
                </div>
                <div><Label>City</Label><Input data-testid="std-cust-city" value={form.customer.city} onChange={(e) => setCust("city", e.target.value)} placeholder="Dubai" /></div>
                <div><Label>Address</Label><Input data-testid="std-cust-address" value={form.customer.address} onChange={(e) => setCust("address", e.target.value)} placeholder="Business Bay" /></div>
              </div>
            </div>

            {/* Lines */}
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Invoice items</h2>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="std-add-line" onClick={addLine}><Plus className="w-4 h-4" /> Add line</Button>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, i) => (
                  <div key={i} data-testid={`std-line-${i}`} className="flex flex-wrap items-start gap-2">
                    <div className="flex-1 min-w-[160px]"><Input data-testid={`std-line-desc-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Description" /></div>
                    <div className="w-16"><Input data-testid={`std-line-qty-${i}`} type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} placeholder="Qty" /></div>
                    <div className="w-28"><Input data-testid={`std-line-price-${i}`} type="number" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} placeholder="Unit price" /></div>
                    <div className="w-36">
                      <Select value={l.vat_rate} onValueChange={(v) => setLine(i, "vat_rate", v)}>
                        <SelectTrigger data-testid={`std-line-vat-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{VAT_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-24 flex items-center h-9 text-sm font-medium justify-end text-foreground">{money((parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0))}</div>
                    <div className="w-8 flex justify-end">
                      {form.lines.length > 1 && <button data-testid={`std-line-del-${i}`} onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive h-9 flex items-center"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Meta */}
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Invoice details</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Issue date</Label><Input data-testid="std-issue-date" type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
                <div><Label>Due date (optional)</Label><Input data-testid="std-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                <div><Label>PO / reference (optional)</Label><Input data-testid="std-po-ref" value={form.po_reference} onChange={(e) => setForm({ ...form, po_reference: e.target.value })} placeholder="PO-123" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Discount</Label><Input data-testid="std-discount" type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></div>
                  <div><Label>Label</Label><Input data-testid="std-discount-label" value={form.discount_label} onChange={(e) => setForm({ ...form, discount_label: e.target.value })} /></div>
                </div>
                <div className="sm:col-span-2"><Label>Notes / payment terms (optional)</Label><Textarea data-testid="std-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment within 30 days…" rows={2} /></div>
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-border bg-card p-5 sticky top-4" data-testid="std-totals">
              <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Totals</h2>
              <div className="space-y-1.5 text-sm">
                <Row label="Subtotal" value={`AED ${money(stdNet + zeroNet + exemptNet)}`} />
                {discount > 0 && <Row label={form.discount_label || "Discount"} value={`AED (${money(discount)})`} />}
                <Row label="Taxable base" value={`AED ${money(taxable)}`} />
                <Row label="VAT @ 5%" value={`AED ${money(vat)}`} />
                {nontax > 0 && <Row label="Zero-rated / exempt" value={`AED ${money(nontax)}`} />}
                <div className="h-px bg-border my-1.5" />
                <Row label="Total" value={`AED ${money(total)}`} strong testid="std-total" />
              </div>
              <Button data-testid="std-save" onClick={save} disabled={busy} className="w-full mt-4 gap-1.5">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editId ? "Save changes" : "Create invoice"}</Button>
              <p className="text-[11px] text-muted-foreground mt-2 text-center">Creating counts as one e-invoice on your plan.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LIST
  return (
    <div data-testid="standard-invoices-page">
      <button onClick={() => navigate("/einvoicing/app")} data-testid="std-list-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Matiqs Qonnect</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><ReceiptText className="w-6 h-6 text-brand" /> Standard invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Simple FTA-compliant tax invoices for any business — no contract or BOQ.</p>
        </div>
        <Button data-testid="std-new-btn" onClick={newInvoice} className="gap-1.5"><Plus className="w-4 h-4" /> New invoice</Button>
      </div>

      {rows.length === 0 ? (
        <div data-testid="std-empty" className="rounded-2xl border border-dashed border-border bg-card py-16 text-center">
          <ReceiptText className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">No invoices yet. Create your first standard tax invoice.</p>
          <Button data-testid="std-empty-new" onClick={newInvoice} className="mt-4 gap-1.5"><Plus className="w-4 h-4" /> New invoice</Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left font-bold">Invoice #</th>
                <th className="px-4 py-2.5 text-left font-bold">Customer</th>
                <th className="px-4 py-2.5 text-left font-bold">Issue date</th>
                <th className="px-4 py-2.5 text-right font-bold">Total (AED)</th>
                {fullService && <th className="px-4 py-2.5 text-center font-bold">Approved ASP</th>}
                <th className="px-4 py-2.5 text-right font-bold">Export</th>
                <th className="px-4 py-2.5 text-right font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} className="border-t border-border" data-testid={`std-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3 font-semibold text-foreground">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{inv.issue_date}</td>
                  <td className="px-4 py-3 text-right font-semibold">{money(inv.total)}</td>
                  {fullService && (
                    <td className="px-4 py-3 text-center">
                      <JamCell sub={subs[inv.id]} busy={subBusy === inv.id} onSubmit={() => submitJam(inv.id)} testnum={inv.invoice_number} onViewClearance={() => navigate("/einvoicing/clearance")} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <IconDl title="PDF" testid={`std-dl-pdf-${inv.invoice_number}`} id={inv.id} fmt="pdf" num={inv.invoice_number} icon={FileText} />
                      <IconDl title="Excel" testid={`std-dl-xlsx-${inv.invoice_number}`} id={inv.id} fmt="xlsx" num={inv.invoice_number} icon={FileSpreadsheet} />
                      <IconDl title="CSV" testid={`std-dl-csv-${inv.invoice_number}`} id={inv.id} fmt="csv" num={inv.invoice_number} icon={Download} />
                      <IconDl title="UBL XML" testid={`std-dl-xml-${inv.invoice_number}`} id={inv.id} fmt="xml" num={inv.invoice_number} icon={FileCode2} />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button className="text-muted-foreground hover:text-rose-500 mr-3" title="Create credit note" data-testid={`std-credit-${inv.invoice_number}`} onClick={() => navigate(`/einvoicing/credit-notes?source_kind=standard&source_id=${inv.id}`)}><Undo2 className="w-4 h-4" /></button>
                    <button className="text-muted-foreground hover:text-foreground mr-3" data-testid={`std-edit-${inv.invoice_number}`} onClick={() => editInvoice(inv.id)}><Pencil className="w-4 h-4" /></button>
                    <button className="text-muted-foreground hover:text-destructive" data-testid={`std-del-${inv.invoice_number}`} onClick={() => del(inv.id)}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const STATUS_STYLE = {
  RECEIVED: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  SENT: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  DELIVERED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  REPORTED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  NOT_COMPLIANT: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  INVALID: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  ACKNOWLEDGED: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  ERROR: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
};
const DONE_OK = ["APPROVED", "REPORTED", "DELIVERED"];
const DONE_BAD = ["REJECTED", "INVALID", "NOT_COMPLIANT", "FAILED"];
const JamCell = ({ sub, busy, onSubmit, testnum, onViewClearance }) => {
  if (sub && sub.status !== "ERROR") {
    return (
      <div className="flex flex-col items-start gap-1">
        <span data-testid={`std-jam-status-${testnum}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[sub.status] || "bg-accent text-muted-foreground"}`}>
          {sub.status === "RECEIVED" && <Loader2 className="w-3 h-3 animate-spin" />}
          {DONE_OK.includes(sub.status) && <CheckCircle2 className="w-3 h-3" />}
          {DONE_BAD.includes(sub.status) && <AlertTriangle className="w-3 h-3" />}
          {sub.status_label || sub.status}
        </span>
        <div className="flex items-center gap-1.5">
          <span data-testid={`std-jam-mode-${testnum}`} className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${sub.mode === "live" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" : "bg-accent text-muted-foreground"}`}>
            {sub.mode_label || (sub.mode === "live" ? "Live · Approved ASP" : "Simulation")}
          </span>
          <button type="button" data-testid={`std-jam-clearance-${testnum}`} onClick={onViewClearance} className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">Clearance →</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <button data-testid={`std-jam-submit-${testnum}`} onClick={onSubmit} disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {sub && sub.status === "ERROR" ? "Retry" : "Submit to FTA"}
      </button>
      {sub && sub.status === "ERROR" && (
        <span className="text-[9px] text-red-600 dark:text-red-400 max-w-[180px] leading-tight">Submission failed — check the ASP connection</span>
      )}
    </div>
  );
};

const Row = ({ label, value, strong, testid }) => (
  <div className="flex items-center justify-between">
    <span className={strong ? "font-bold text-foreground" : "text-muted-foreground"}>{label}</span>
    <span data-testid={testid} className={strong ? "font-bold text-foreground" : "text-foreground"}>{value}</span>
  </div>
);

const IconDl = ({ title, testid, id, fmt, num, icon: Icon }) => (
  <button title={title} data-testid={testid} onClick={() => downloadFile(`/einvoice/standard-invoices/${id}/download?fmt=${fmt}`, `${num}.${fmt}`)} className="p-1.5 rounded-lg text-muted-foreground hover:text-brand hover:bg-accent transition-colors">
    <Icon className="w-4 h-4" />
  </button>
);
